import * as $ from "jquery";
import { uniqueId } from "lodash";
import * as React from "react";
import { Component } from "react";
import { findDOMNode } from "react-dom";
import { Keys } from "~/common/enums";
import { editorOptions } from "~/EasyProjects/Details/FieldEditorOptions";
import { defaultMaxDate, defaultMinDate } from "~/EasyProjectsCommon/Dates/consts";
import { dateUtility } from "~/EasyProjectsCommon/Dates/DateUtility";
import "~/EasyProjectsCommon/Dates/setEpCalendar";
import { currentCultureSettings } from "~/EasyProjectsCommon/setCulture";
import { closeModalPopupEvent } from "~/sharedComponents/modalPopup/events";
import { withValidation } from "~/sharedHOC/withValidation/withValidation";
import { domUtils } from "~/utils/domUtils";
import * as styles from "./DatepickerInput.less";
import { Input, InputProps } from "./Input";

export type DatepickerOptions = JQueryUI.DatepickerOptions & {
    autoOpen?: boolean;
    doKeyEnter?: F1<React.KeyboardEvent<HTMLElement>, void>;

    /** The earliest selectable date, @see defaultMinDate by default */
    minDate?: Date;
    /** Tooltip to be shown over dates less than minDate*/
    minDateMessage?: string;

    /** The latest selectable date, @see defaultMaxDate by default */
    maxDate?: Date;
    /** Tooltip to be shown over dates greater than maxDate*/
    maxDateMessage?: string;
};

interface DatepickerInputProps extends InputProps<Date> {
    renderInline?: boolean;
    options?: DatepickerOptions;
    onSelect?: Function;
    calendar?: CalendarWithHolidays;
    onChangeAction?: (value: Date, textValue?: string) => any;
}

type State = {
    date: Date;
    dateText: string;
};

export const dateViewerFormatter = (date: Date) => {
    if (!date) {
        return "";
    }

    let result = dateUtility.formatAsShort(date);
    if (!dateUtility.isCompareWithCurrentYear(date)) {
        result += `, ${date.getFullYear()}`;
    }
    return result;
};

const parseDate = (dateText: string) => {
    if (dateText === "") {
        return null;
    }

    return $.datepicker.parseDate(currentCultureSettings.dateFormat, dateText);
};

export const formatDate = (date: Date) => {
    if (!date) {
        return "";
    }

    return $.datepicker.formatDate(currentCultureSettings.dateFormat, date);
};

export class DatepickerInput extends Component<DatepickerInputProps, State> {
    inputRef: HTMLInputElement;
    inlineDpRef: React.RefObject<HTMLDivElement>;
    namespace: string;
    subscription: boolean = false;

    constructor(props: DatepickerInputProps) {
        super(props);

        this.inlineDpRef = React.createRef();
        this.namespace = uniqueId(".datepickerInput");

        this.onBlur = this.onBlur.bind(this);
        this.onClick = this.onClick.bind(this);

        this.enhanceInput = this.enhanceInput.bind(this);
        this.closeDatepicker = this.closeDatepicker.bind(this);
        this.state = {
            date: this.props.value,
            dateText: formatDate(this.props.value),
        };
    }

    static getDerivedStateFromProps(nextProps: DatepickerInputProps, prevState: State) {
        if (!dateUtility.strictEqual(nextProps.value, prevState.date)) {
            return {
                date: nextProps.value,
                dateText: formatDate(nextProps.value),
            };
        }

        return null;
    }

    get input() {
        return this.props.renderInline ? this.inlineDpRef.current : this.inputRef;
    }

    get dpDiv() {
        return $.datepicker._getInst(this.input).dpDiv;
    }

    enhanceInput(ref: HTMLInputElement) {
        this.inputRef = ref;

        if (this.props.refCallback) {
            this.props.refCallback(ref);
        }
    }

    onChangeAction = (dateText: string) => {
        let date = this.props.value;
        try {
            date = parseDate(dateText);
        } catch {
            /* just ignore parsing errors */
        }

        this.setState({ date, dateText });

        if (this.props.onChangeAction) {
            this.props.onChangeAction(date, dateText);
        }

        if (this.props.renderInline) {
            $(this.input).datepicker("setDate", date);
            $(this.input).datepicker("refresh");
        }
    };

    closeDatepicker() {
        $(this.input).datepicker("hide");
    }

    openDatepicker() {
        $(this.input).datepicker("show");
    }

    updateDatepickerPosition() {
        $(this.input).datepicker("updatePosition");
    }

    hide() {
        this.dpDiv.addClass(styles.hidden);
    }

    unhide() {
        this.dpDiv.removeClass(styles.hidden);
    }

    trackScroll = () => {
        const element = findDOMNode(this.input) as HTMLElement;

        if (domUtils.isVisible(element)) {
            this.unhide();
            this.updateDatepickerPosition();
        } else {
            this.hide();
        }
    };

    componentDidMount() {
        window.addEventListener(closeModalPopupEvent, this.closeDatepicker);
        const { options, calendar } = this.props;

        const datepickerOptions = {
            ...options,
            minDate: options?.minDate ?? defaultMinDate,
            maxDate: options?.maxDate ?? defaultMaxDate,
            onClose: (dateText: string, inst: any) => {
                if (options?.appendToBody) {
                    window.removeEventListener("scroll", this.trackScroll, true);
                    this.unhide();
                }

                options?.onClose?.(dateText, inst);
            },
        };

        datepickerOptions.beforeShow = (...args): any => {
            if (!this.subscription) {
                this.subscription = true;

                $(this.input).on(`keyup${this.namespace}`, event => {
                    if (event.key === Keys.Escape) {
                        $(this.input).off(`keyup${this.namespace}`);
                        this.subscription = false;
                        event.stopPropagation();
                    }
                });
            }

            if (options?.appendToBody) {
                window.addEventListener("scroll", this.trackScroll, true);
            }

            if (this.props.options?.beforeShow) {
                return this.props.options.beforeShow(...args);
            }
        };

        datepickerOptions.beforeShowDay = (date: Date) => {
            if (
                this.props.options?.minDateMessage &&
                this.props.options?.minDate &&
                date < this.props.options.minDate
            ) {
                return [false, "", this.props.options?.minDateMessage];
            }
            if (
                this.props.options?.maxDateMessage &&
                this.props.options?.maxDate &&
                date > this.props.options.maxDate
            ) {
                return [false, "", this.props.options?.maxDateMessage];
            }
            return [true, "", ""];
        };

        datepickerOptions.onSelect = dateText => {
            this.onChangeAction(dateText);
            if (this.props.onSelect) {
                this.props.onSelect();
            }
        };

        $(this.input).datepicker(datepickerOptions);

        if (calendar) {
            $(this.input).setEpCalendar(calendar);
        }

        if (datepickerOptions.autoOpen) {
            this.openDatepicker();
        }

        this.dpDiv.on(`mousedown${this.namespace} mouseup${this.namespace}`, event => {
            event.stopPropagation();
        });
    }

    componentWillUnmount() {
        this.dpDiv.off(this.namespace);

        if (this.subscription) {
            this.subscription = false;
            $(this.input).off(`keyup${this.namespace}`);
        }

        $(this.input).datepicker("hide").datepicker("destroy");
        window.removeEventListener(closeModalPopupEvent, this.closeDatepicker);
    }

    componentDidUpdate(prevProps: DatepickerInputProps) {
        if (this.props.showValidation !== prevProps.showValidation) {
            this.updateDatepickerPosition();
        }

        if (this.props.calendar && this.props.calendar !== prevProps.calendar) {
            $(this.input).setEpCalendar(this.props.calendar);
        }

        if (!dateUtility.equal(this.props.options?.minDate, prevProps.options?.minDate)) {
            this.updateMinOrMaxDate("minDate", this.props.options?.minDate);
        }

        if (!dateUtility.equal(this.props.options?.maxDate, prevProps.options?.maxDate)) {
            this.updateMinOrMaxDate("maxDate", this.props.options?.maxDate);
        }

        if (
            this.props.options?.minDateMessage !== prevProps.options?.minDateMessage ||
            this.props.options?.maxDateMessage !== prevProps.options?.maxDateMessage
        ) {
            this.updateMinOrMaxDateMessage(this.props.options?.minDateMessage, this.props.options?.maxDateMessage);
        }
    }

    updateMinOrMaxDate = (key: "minDate" | "maxDate", date: Date) => {
        const currentValue = this.inputRef.value;
        $(this.input).datepicker("option", key, date);
        // jquery might change current date in input silently if new minDate\maxDate is greater\less than current date.
        // revert current date back in this case.
        if (currentValue !== this.inputRef.value) {
            this.inputRef.value = currentValue;
        }
    };

    updateMinOrMaxDateMessage = (minDateMessage?: string, maxDateMessage?: string) => {
        const currentValue = this.inputRef.value;

        $(this.input).datepicker("beforeShowDay", (date: Date) => {
            if (minDateMessage && date < this.props.options.minDate) {
                return [false, "", minDateMessage];
            }
            if (maxDateMessage && date > this.props.options.maxDate) {
                return [false, "", maxDateMessage];
            }
            return [true, "", ""];
        });

        if (currentValue !== this.inputRef.value) {
            this.inputRef.value = currentValue;
        }
    };

    onBlur(event: React.FocusEvent<HTMLElement>) {
        // close datepicker when browser window lose focus because
        // it might lead to infinite cycle of closing/opening datepicker
        // when user clicks on another datepicker (see bug 35744:IE11: Browser freezes when go from one date picker to other)
        // when user clicks somewhere outside browser window then activeElement is input
        // but blur event is triggered on it.
        const isBrowserLostFocus = document.activeElement === this.input;

        if (this.dpDiv.is(":visible") && !isBrowserLostFocus && !this.props.renderInline) {
            // do nothing when datepicker input lose focus if calendar dd is visible
            // for cases when user clicks somewhere inside calendar.
            this.input.focus();
            // required on timelog details to prevent leaving of edit mode
            event.stopPropagation();
            return;
        }

        if (this.props.onBlurAction) {
            this.props.onBlurAction(event);
        }
    }

    onClick() {
        if (!this.props.renderInline) {
            this.openDatepicker();
        }

        if (this.props.onClickAction) {
            this.props.onClickAction();
        }
    }

    render() {
        const { renderInline, options, value, onChangeAction, onBlurAction, ...inputProps } = this.props;
        return (
            <>
                <Input
                    {...inputProps}
                    onBlurAction={this.onBlur}
                    value={this.state.dateText}
                    onChangeAction={this.onChangeAction}
                    onClickAction={this.onClick}
                    refCallback={this.enhanceInput}
                    maxLength={editorOptions.date.maxLength}
                    placeholder={editorOptions.date.getFormat()}
                />
                {renderInline && (
                    <div className={styles.inline_datepicker} ref={this.inlineDpRef} data-testid="inline-datepicker" />
                )}
            </>
        );
    }
}

export const DatePickerWithValidation = withValidation(DatepickerInput);
